import React, { useState, useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import { ChevronRight, Phone, Mail, MapPin, Clock, Send, Building2 } from "lucide-react";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import { getCmsPage, getTeamPublic } from "../lib/api";
import { useTheme } from "../context/ThemeContext";
import { toast } from "sonner";

function TeamBlock({ role }) {
    const [team, setTeam] = useState([]);
    useEffect(() => {
        getTeamPublic().then(r => {
            let members = r.data || [];
            if (role) members = members.filter(m => m.role === role);
            setTeam(members);
        }).catch(() => {});
    }, [role]);

    if (!team.length) return null;
    return (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 my-6" data-testid="cms-team-block">
            {team.map(m => (
                <div key={m.id} className="bg-white rounded-xl border border-gray-100 p-5 flex flex-col items-center text-center hover:shadow-md hover:border-green-200 transition-all" data-testid={`cms-team-card-${m.id}`}>
                    {m.photo_url ? (
                        <img src={m.photo_url} alt={m.name} className="w-20 h-20 rounded-full object-cover mb-3" />
                    ) : (
                        <div className="w-20 h-20 rounded-full bg-green-100 flex items-center justify-center text-green-700 font-bold text-xl mb-3">
                            {m.name?.split(" ").map(w => w[0]).join("").slice(0, 2)}
                        </div>
                    )}
                    <h4 className="font-semibold text-gray-900 text-base">{m.name}</h4>
                    <p className="text-sm text-green-600 font-medium">{m.title || m.role_label}</p>
                    {m.bio && <p className="text-xs text-gray-500 mt-1 line-clamp-2">{m.bio}</p>}
                    <div className="flex flex-col gap-1.5 mt-3 w-full">
                        {m.phone && (
                            <a href={`tel:${m.phone}`} className="flex items-center justify-center gap-1.5 text-sm text-gray-700 hover:text-green-600 transition-colors bg-gray-50 rounded-lg py-2">
                                <Phone size={14} className="text-green-500" /> {m.phone}
                            </a>
                        )}
                        {m.email && (
                            <a href={`mailto:${m.email}`} className="flex items-center justify-center gap-1.5 text-sm text-gray-700 hover:text-green-600 transition-colors bg-gray-50 rounded-lg py-2">
                                <Mail size={14} className="text-green-500" /> {m.email}
                            </a>
                        )}
                    </div>
                </div>
            ))}
        </div>
    );
}

function MapBlock() {
    return (
        <div className="my-6 rounded-xl overflow-hidden border border-gray-100" data-testid="cms-map-block" style={{ filter: "grayscale(100%)" }}>
            <iframe
                src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d2556.8!2d14.0926!3d50.1468!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x470bb417c8d1a9a3%3A0x1!2sHutsk%C3%A1%20368%2C%20272%2001%20Kladno!5e0!3m2!1scs!2scz!4v1700000000000"
                width="100%" height="350" style={{ border: 0 }} allowFullScreen loading="lazy"
                referrerPolicy="no-referrer-when-downgrade" title="Mapa Solar-Green - Hutská 368, Kladno"
            />
        </div>
    );
}

function BusinessPhotoBlock() {
    return (
        <div className="my-6 rounded-xl overflow-hidden border border-gray-100" data-testid="cms-photo-block">
            <img
                src="https://eshop.solar-green.cz/img/cms/hutska2.jpg"
                alt="Provozovna Solar-Green - Hutská 368, Kladno"
                className="w-full h-auto object-cover"
                loading="lazy"
            />
            <div className="bg-white px-4 py-2.5 text-sm text-gray-500 flex items-center gap-2">
                <MapPin size={14} className="text-green-500" />
                <span>Provozovna Solar-Green.cz &ndash; Hutská 368, Kladno</span>
            </div>
        </div>
    );
}

function OpeningHoursBlock() {
    const hours = [
        { day: "Pondělí", time: "8:00 - 17:00" },
        { day: "Úterý", time: "8:00 - 17:00" },
        { day: "Středa", time: "8:00 - 17:00" },
        { day: "Čtvrtek", time: "8:00 - 17:00" },
        { day: "Pátek", time: "8:00 - 16:00" },
        { day: "Sobota", time: "Zavřeno" },
        { day: "Neděle", time: "Zavřeno" },
    ];
    const today = new Date().getDay();
    const dayMap = [6, 0, 1, 2, 3, 4, 5]; // JS: 0=Sun
    return (
        <div className="bg-white rounded-xl border border-gray-100 p-5 my-6" data-testid="cms-hours-block">
            <div className="flex items-center gap-2 mb-3">
                <Clock size={16} className="text-green-600" />
                <h4 className="font-semibold text-gray-900">Otevírací doba</h4>
            </div>
            <div className="space-y-1.5">
                {hours.map((h, i) => (
                    <div key={i} className={`flex justify-between text-sm px-3 py-1.5 rounded-lg ${dayMap[today] === i ? "bg-green-50 text-green-800 font-semibold" : "text-gray-600"}`}>
                        <span>{h.day}</span>
                        <span className={h.time === "Zavřeno" ? "text-red-500" : ""}>{h.time}</span>
                    </div>
                ))}
            </div>
        </div>
    );
}

function CompanyInfoBlock() {
    const { theme } = useTheme();
    return (
        <div className="bg-white rounded-xl border border-gray-100 p-5 my-6 space-y-3" data-testid="cms-company-block">
            <div className="flex items-center gap-2 mb-2">
                <Building2 size={16} className="text-green-600" />
                <h4 className="font-semibold text-gray-900">{theme.company_name || "SOLAR GREEN s.r.o."}</h4>
            </div>
            <div className="space-y-2 text-sm text-gray-600">
                {theme.office_address && <p className="flex items-center gap-2"><MapPin size={14} className="text-green-500 flex-shrink-0" /> Office: {theme.office_address}</p>}
                {theme.billing_address && <p className="flex items-center gap-2"><MapPin size={14} className="text-green-500 flex-shrink-0" /> Fakturace: {theme.billing_address}</p>}
                <p className="flex items-center gap-2"><Phone size={14} className="text-green-500 flex-shrink-0" /> <a href={`tel:${theme.phone}`} className="hover:text-green-600">{theme.phone || "+420 608 808 815"}</a></p>
                <p className="flex items-center gap-2"><Mail size={14} className="text-green-500 flex-shrink-0" /> <a href={`mailto:${theme.email}`} className="hover:text-green-600">{theme.email || "info@solar-green.cz"}</a></p>
            </div>
            {(theme.ico || theme.dic) && (
                <div className="text-xs text-gray-400 pt-2 border-t border-gray-50">
                    {theme.ico && `IČ: ${theme.ico}`}{theme.ico && theme.dic && " | "}{theme.dic && `DIČ: ${theme.dic}`}
                </div>
            )}
        </div>
    );
}

function ContactFormBlock() {
    const [form, setForm] = useState({ name: "", email: "", phone: "", message: "" });
    const [sent, setSent] = useState(false);
    const handleSubmit = (e) => {
        e.preventDefault();
        if (!form.name || !form.email || !form.message) { toast.error("Vyplňte povinná pole"); return; }
        toast.success("Zpráva odeslána!");
        setSent(true);
    };
    if (sent) return (
        <div className="bg-green-50 border border-green-200 rounded-xl p-6 my-6 text-center" data-testid="cms-form-sent">
            <p className="text-green-800 font-semibold">Děkujeme za zprávu!</p>
            <p className="text-sm text-green-600 mt-1">Odpovíme co nejdříve.</p>
        </div>
    );
    return (
        <form onSubmit={handleSubmit} className="bg-white rounded-xl border border-gray-100 p-5 my-6 space-y-3" data-testid="cms-contact-form">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                    <label className="text-xs font-medium text-gray-500 mb-1 block">Jméno *</label>
                    <input value={form.name} onChange={e => setForm(f => ({...f, name: e.target.value}))} required
                        className="w-full h-10 px-3 text-sm border rounded-lg focus:outline-none focus:ring-2 focus:ring-green-200" placeholder="Vaše jméno" data-testid="contact-name" />
                </div>
                <div>
                    <label className="text-xs font-medium text-gray-500 mb-1 block">Email *</label>
                    <input type="email" value={form.email} onChange={e => setForm(f => ({...f, email: e.target.value}))} required
                        className="w-full h-10 px-3 text-sm border rounded-lg focus:outline-none focus:ring-2 focus:ring-green-200" placeholder="vas@email.cz" data-testid="contact-email" />
                </div>
            </div>
            <div>
                <label className="text-xs font-medium text-gray-500 mb-1 block">Telefon</label>
                <input value={form.phone} onChange={e => setForm(f => ({...f, phone: e.target.value}))}
                    className="w-full h-10 px-3 text-sm border rounded-lg focus:outline-none focus:ring-2 focus:ring-green-200" placeholder="+420..." data-testid="contact-phone" />
            </div>
            <div>
                <label className="text-xs font-medium text-gray-500 mb-1 block">Zpráva *</label>
                <textarea value={form.message} onChange={e => setForm(f => ({...f, message: e.target.value}))} required rows={4}
                    className="w-full px-3 py-2 text-sm border rounded-lg resize-none focus:outline-none focus:ring-2 focus:ring-green-200" placeholder="Vaše zpráva..." data-testid="contact-message" />
            </div>
            <button type="submit" className="flex items-center gap-2 px-5 py-2.5 bg-green-600 text-white text-sm font-semibold rounded-lg hover:bg-green-700 transition-colors" data-testid="contact-submit">
                <Send size={14} /> Odeslat zprávu
            </button>
        </form>
    );
}

function renderContent(html, pageTitle) {
    if (!html) return null;
    // Remove leading h2 that duplicates the page title
    let cleaned = html;
    if (pageTitle) {
        const re = new RegExp(`^\\s*<h2[^>]*>\\s*${pageTitle.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}\\s*<\\/h2>\\s*`, 'i');
        cleaned = cleaned.replace(re, '');
    }
    // Split by dynamic blocks
    const parts = cleaned.split(/(<div data-block="[^"]*"[^>]*><\/div>)/g);
    return parts.map((part, i) => {
        const blockMatch = part.match(/data-block="([^"]*)"(?:\s+data-role="([^"]*)")?/);
        if (blockMatch) {
            const type = blockMatch[1];
            const role = blockMatch[2] || null;
            switch (type) {
                case "team-members": return <TeamBlock key={i} role={role} />;
                case "map": return <MapBlock key={i} />;
                case "business-photo": return <BusinessPhotoBlock key={i} />;
                case "contact-form": return <ContactFormBlock key={i} />;
                case "opening-hours": return <OpeningHoursBlock key={i} />;
                case "company-info": return <CompanyInfoBlock key={i} />;
                default: return null;
            }
        }
        return <div key={i} className="sg-cms-content prose max-w-none" dangerouslySetInnerHTML={{ __html: part }} />;
    });
}

export default function CmsPage() {
    const { slug } = useParams();
    const [page, setPage] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    useEffect(() => {
        setLoading(true);
        setError(null);
        getCmsPage(slug)
            .then(r => setPage(r.data))
            .catch(() => setError("Stránka nenalezena"))
            .finally(() => setLoading(false));
    }, [slug]);

    return (
        <div className="min-h-screen" style={{ backgroundColor: "var(--sg-bg-light)" }}>
            <Navbar />
            <div className="bg-gray-50 border-b border-gray-100">
                <div className="max-w-4xl mx-auto px-4 py-3 flex items-center gap-2 text-sm text-gray-500">
                    <Link to="/" className="hover:text-green-700">Domů</Link>
                    <ChevronRight size={12} />
                    <span className="text-gray-800 font-medium">{page?.title || slug}</span>
                </div>
            </div>
            <div className="max-w-4xl mx-auto px-4 py-8">
                {loading ? (
                    <div className="space-y-4">
                        <div className="h-8 w-1/3 bg-gray-200 rounded animate-pulse" />
                        <div className="h-4 w-full bg-gray-100 rounded animate-pulse" />
                        <div className="h-4 w-3/4 bg-gray-100 rounded animate-pulse" />
                    </div>
                ) : error ? (
                    <div className="text-center py-20">
                        <p className="text-lg text-gray-500">{error}</p>
                        <Link to="/" className="text-green-600 hover:underline mt-2 inline-block">Zpět na hlavní stránku</Link>
                    </div>
                ) : (
                    <div>
                        <h1 className="text-2xl font-bold text-gray-900 mb-6">{page.title}</h1>
                        {renderContent(page.content || page.body, page.title)}
                    </div>
                )}
            </div>
            <Footer />
        </div>
    );
}
